import { Observable } from 'rxjs';
import {
  HUGINN_LOAD_IS_SLOW,
  LWR_AUTH,
  LWR_IS_SLOW,
  LWR_IS_STALLED,
} from '@pass-frontend/features/authentication/util';
import { env } from '@pass-frontend/features/configuration/util';
import {
  after,
  createProcessEvents,
  eventBus,
} from '@pass-frontend/utility/event-bus';
import { pollUntil, trace } from '@pass-frontend/utility/swiss-knife';

declare global {
  interface Window {
    huginn: {
      provisionOIDCSession: any;
    };
  }
}
interface SessionProvision {
  exchangeTimeout?: number;
  productPath?: string;
  rpTokenSuffix: string;
  sessionUri: string;
  tokenUri: string;
}
interface ProvisionedOIDCSession {
  meta: {
    success: boolean;
    correlationId?: string;
  };
  data: {
    rsMetadata: Record<string, string>;
    uiMetadata: Record<string, string>;
  };
}
interface ProvisionedOIDCSessionError {
  error: string;
  errorDescription: string;
  state?: string;
}
export type ProvisionedOIDCSessionResult =
  | ProvisionedOIDCSession
  | ProvisionedOIDCSessionError;

const {
  HUGINN_TOKEN_SUFFIX,
  HUGINN_SESSION_ENDPOINT,
  HUGINN_TOKEN_ENDPOINT,
  BASE_PATH,
} = env();

export const HUGINN_LOAD = createProcessEvents<void, void, Error>(
  'huginn/load',
);

export const provisionSession = async (returnTo: string): Promise<boolean> => {
  const sessionProvisionParams: SessionProvision = {
    rpTokenSuffix: HUGINN_TOKEN_SUFFIX,
    sessionUri: HUGINN_SESSION_ENDPOINT,
    tokenUri: HUGINN_TOKEN_ENDPOINT,
    productPath: returnTo,
  };

  const warnSlow = after(LWR_IS_SLOW, () => {
    trace(`error: LWR provisionOIDCSession exceeded ${LWR_IS_SLOW}ms`);
  }).subscribe();
  const warnStalled = after(LWR_IS_STALLED, () => {
    // TODO send to authentication to spare them from a stalled-looking screen
    trace(`error: LWR provisionOIDCSession stalled at ${LWR_IS_STALLED}ms`);
  }).subscribe();

  return pollUntil(
    () => !!window.huginn?.provisionOIDCSession,
    // PASS-6924
    LWR_IS_STALLED,
  ).then(() =>
    window.huginn
      .provisionOIDCSession(sessionProvisionParams)
      .then((response: ProvisionedOIDCSession) => {
        // This may not indicate a final success
        // This doesn't: {"meta":{"success":true},"data":{"uiMetadata":{"methodUsed":"interaction"}}}
        // This does: {"meta":{"success":true},"data":{"rsMetadata":{"sid":"a8abc45d-1342-4402-8412-90bc5e5b11f3","iss":"https://token.integration.werally.in","sub":"302eb0d7-4045-4a30-86ef-9ecf499867f1","scopes":["openid","elid:e06682ce-5ecf-4ed7-af02-8ac8f40909b3","rfq:advantage.findcare","rfq:advantage.dashboard","rfq:advantage.rewards","rfq:advantage.pricecare","rfq:advantage.default","rfq:advantage.one_pass","rfq:advantage.resources","rfq:advantage.wellness"],"uctx":null,"azp":"advantage_web_rp_client","exp":"2022-12-13T11:27:22Z","iat":"2022-12-12T23:29:54Z","type":"access","impId":null,"rel":"self"},"idMetadata":{"sid":"a8abc45d-1342-4402-8412-90bc5e5b11f3","iss":"https://token.integration.werally.in","sub":"302eb0d7-4045-4a30-86ef-9ecf499867f1","aud":["advantage_web_rp_client"],"exp":"2022-12-13T11:27:22Z","iat":"2022-12-12T23:29:54Z","auth_time":"2022-12-12T23:29:54Z","impId":null,"rel":"self","federatedId":null,"email":null},"uiMetadata":{"methodUsed":"interaction"}}}
        if (response?.meta?.success) {
          if (response?.data?.rsMetadata) {
            eventBus.trigger(LWR_AUTH.next(response.data.rsMetadata));
            eventBus.trigger(LWR_AUTH.complete());
          } else {
            // note if we're not logged in we'll be redirected soon
            eventBus.trigger(LWR_AUTH.error(response.data));
          }
        }
        return response?.meta?.success || false;
      })
      .catch((e: Error) => {
        eventBus.trigger(LWR_AUTH.error(e));
        return false;
      })
      .finally(() => {
        warnSlow.unsubscribe();
        warnStalled.unsubscribe();
      }),
  );
};

/**
 * Dynamically load external scripts and trigger an event for when they are loaded
 */
export const loadExternalScripts = (src, id): Promise<void> => {
  return new Promise((resolve, reject) => {
    // check to see if script src already exists
    const resource: HTMLScriptElement = document.querySelector(
      `script[src="${src}"]`,
    );
    if (!resource) {
      // Create script
      const script: HTMLScriptElement = Object.assign(
        document.createElement('script'),
        {
          src,
          id,
          type: 'text/javascript',
          async: true,

          onload: () => {
            /* istanbul ignore next */
            resolve(window[`${id}`]);
          },
          onerror: () => {
            /* istanbul ignore next */
            reject(new Error(`Script: ${src} not loaded`));
          },
        },
      );

      // Add script to head -  If you require a script to be added to body use a hook
      // since that indicates your script only needs to load for a specific component within React.
      // this is specifically for loading an external script before react.
      document.head.appendChild(script);
    }
  });
};

export const HUGINN_INIT_WINDOW_KEY = 'initHuginn';

// Loads Huginn - idempotently when called in listenBlocking mode.
export const huginnLoader: () => Observable<undefined> = () => {
  return new Observable((notify) => {
    const huginnUrl = `/huginn`;
    const warnSlow = after(HUGINN_LOAD_IS_SLOW, () => {
      trace(
        `Error: huginn library load call exceeded ${HUGINN_LOAD_IS_SLOW}ms`,
      );
    }).subscribe();

    // we're ready to provision when this gets called
    Object.assign(window, {
      [HUGINN_INIT_WINDOW_KEY]: {
        onLoad() {
          warnSlow.unsubscribe();
          eventBus.trigger(HUGINN_LOAD.complete());
        },
        provision: {
          productPath: BASE_PATH,
        },
      },
    });

    // the second part of the load is async, so they're not both awaited
    loadExternalScripts(huginnUrl, 'huginn')
      .then(() => {
        eventBus.trigger(HUGINN_LOAD.next()); // the new script tag for huggin.1.5.0 has been written
      })
      .catch((e) => {
        eventBus.trigger(HUGINN_LOAD.error(e));
        notify.error(e);
      });
  });
};
